import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import Pill from '../components/Pill';
import { fmt, fmtCost } from '../utils/format';

// ── Customers waiting on a code (inline expand) ───────────────────────────────
const PROBLEM_STAGE     = 'Problem Encountered';
const PROBLEM_COMMENT   = 'No stock with supplier';
const BACKORDER_STAGE   = 'HC - Back Order';
const BACKORDER_COMMENT = 'No stock with supplier — placed as HC Back Order';

const FulfilmentDetail = ({ code, supplierName }) => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null);   // `${order_ref}_${stage}` mid-request
  const [done, setDone]       = useState({});     // `${order_ref}_${stage}` -> error text

  useEffect(() => {
    // Filter on supplier as well as code (item_code is not unique across
    // suppliers), and on ordered_for=false: an order already on a Smart Buyer
    // PO is being dealt with and is only waiting on the supplier. What needs
    // chasing is the demand nobody has ordered against yet.
    supabase
      .from('v_smart_buyer_all_fulfilment')
      .select('*')
      .eq('item_code', code)
      .eq('supplier_name', supplierName)
      .eq('ordered_for', false)
      .then(({ data }) => { setRows(data || []); setLoading(false); });
  }, [code, supplierName]);

  const setStage = async (r, stage, comment) => {
    const key = `${r.order_ref}_${stage}`;
    setBusy(key);
    // Queued rather than called directly: the browser has no Cin7
    // credentials, so set_stage_from_queue.py applies it server-side on the
    // poller's ~1 minute cycle.
    const { error } = await supabase.from('sales_order_stage_updates').insert({
      order_ref:        r.order_ref,
      order_id:         r.order_id,
      stage:            stage,
      internal_comment: comment,
    });

    if (error) {
      // Failures stay on screen -- they need acting on, and clearing them
      // after a few seconds would hide the fact that nothing was queued.
      setDone((d) => ({ ...d, [key]: error.message }));
      setBusy(null);
      return;
    }

    // Success is transient: hold "Submitting..." briefly so the click clearly
    // registered, then drop back to the button. The row itself stays either
    // way -- it is listed because stock cannot fulfil it, which is still true
    // after the stage changes.
    setTimeout(() => setBusy((b) => (b === key ? null : b)), 3000);
  };

  if (loading) return <div className="detail-loading">Loading orders...</div>;
  if (!rows.length) return <div className="detail-loading">All open orders are already on a Smart Buyer PO</div>;

  return (
    <div className="detail-wrap">
      <div className="detail-title">Not yet ordered for — {code}</div>
      <table className="detail-table">
        <thead>
          <tr>
            <th>Order Ref</th>
            <th>Status</th>
            <th>Stage</th>
            <th>Company</th>
            <th>Customer</th>
            <th className="num">Qty Outstanding</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td><span className="code-badge code-badge--sm">{r.order_ref || r.order_id}</span></td>
              <td>{r.order_status || '—'}</td>
              <td>{r.order_stage || '—'}</td>
              <td>{r.company || '—'}</td>
              <td>{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</td>
              <td className="num bold">{fmt(r.qty_outstanding)}</td>
              <td>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {[
                    { stage: PROBLEM_STAGE,   comment: PROBLEM_COMMENT,   label: 'Problem Encountered' },
                    { stage: BACKORDER_STAGE, comment: BACKORDER_COMMENT, label: 'HC Back Order' },
                  ].map(({ stage, comment, label }) => {
                    const key        = `${r.order_ref}_${stage}`;
                    const err        = done[key];
                    const alreadySet = r.order_stage === stage;
                    if (alreadySet) {
                      return <span key={stage} style={{ fontSize: 11, color: '#888' }}>Already {label}</span>;
                    }
                    if (err) {
                      return <span key={stage} style={{ fontSize: 11, color: '#f87171' }}>✕ {err}</span>;
                    }
                    return (
                      <button
                        key={stage}
                        className="action-btn action-btn--ghost"
                        style={{ fontSize: 10, padding: '2px 10px' }}
                        disabled={busy === key}
                        onClick={() => setStage(r, stage, comment)}
                      >
                        {busy === key ? 'Submitting....' : `Set to ${label}`}
                      </button>
                    );
                  })}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Orders Without Stock table ────────────────────────────────────────────────
const OrdersWithoutStockTable = ({ data, loading, ordersByRow }) => {
  const [expanded, setExpanded] = useState(null);

  if (loading) return <div className="loading-row">Loading...</div>;
  if (!data.length) return <div className="empty-row">✓ All customer orders have sufficient stock</div>;

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Supplier</th>
            <th>Code</th>
            <th>Product</th>
            <th>Awaiting Order</th>
            <th className="num">Cust Orders</th>
            <th className="num">Qty Needed</th>
            <th className="num">SOH</th>
            <th className="num">Available</th>
            <th className="num">Supplier SOH</th>
            <th className="num">Std Cost</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => {
            // Key on supplier + code: the same code can appear under more
            // than one supplier, and a bare code key would collide.
            const rowKey = `${row.supplier_name}|${row.code}`;
            return (
              <React.Fragment key={rowKey}>
                <tr
                  className={`data-row ${expanded === rowKey ? 'data-row--expanded' : ''}`}
                  onClick={() => setExpanded(expanded === rowKey ? null : rowKey)}
                >
                  <td><Pill label={row.supplier_name || '—'} color="blue" /></td>
                  <td><span className="code-badge">{row.code}</span></td>
                  <td className="product-name">{row.product_name}</td>
                  <td>
                    {(ordersByRow[rowKey] || []).length
                      ? (ordersByRow[rowKey] || []).map((ref) => (
                          <span key={ref} className="code-badge code-badge--sm" style={{ marginRight: 4 }}>{ref}</span>
                        ))
                      : '—'}
                  </td>
                  <td className="num">{row.cust_orders}</td>
                  <td className="num bold">{fmt(row.qty_outstanding)}</td>
                  <td className="num">{fmt(row.soh)}</td>
                  <td className={`num ${Number(row.available) < 0 ? 'neg' : ''}`}>{fmt(row.available)}</td>
                  <td className={`num ${!(Number(row.supplier_soh) > 0) ? 'neg' : ''}`}>
                    {row.supplier_soh == null ? 'Not Stocked' : fmt(row.supplier_soh)}
                  </td>
                  <td className="num">{fmtCost(row.std_cost)}</td>
                </tr>
                {expanded === rowKey && (
                  <tr className="detail-row">
                    <td colSpan={10}>
                      <FulfilmentDetail code={row.code} supplierName={row.supplier_name} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const CustomerOrdersNoStock = () => {
  const navigate = useNavigate();
  const [data, setData]               = useState([]);
  const [ordersByRow, setOrdersByRow] = useState({});
  const [loading, setLoading]         = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [supplier, setSupplier]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);

    // Fulfilment rows are fetched once and grouped here rather than per row:
    // the expandable detail already queries them one code at a time, and
    // repeating that for every row just to show the refs would be a request
    // per line. Keyed by supplier|code because item_code is not unique across
    // suppliers.
    const [{ data: rows }, { data: fulfil }] = await Promise.all([
      supabase.from('v_smart_buyer_all').select('*')
        .order('supplier_name').order('product_name'),
      // ordered_for=false only, so the column matches what the drill-down
      // shows. Listing every open order instead would bury the ones needing
      // action -- UP16372 has 9, but 7 are already on POs and only 2 need
      // chasing.
      supabase.from('v_smart_buyer_all_fulfilment')
        .select('supplier_name, item_code, order_ref')
        .eq('ordered_for', false),
    ]);

    const byRow = {};
    for (const f of (fulfil || [])) {
      const k = `${f.supplier_name}|${f.item_code}`;
      if (!byRow[k]) byRow[k] = [];
      if (f.order_ref && !byRow[k].includes(f.order_ref)) byRow[k].push(f.order_ref);
    }

    setData(rows || []);
    setOrdersByRow(byRow);
    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => { load(); }, [load]);

  const suppliers = useMemo(() =>
    [...new Set(data.map(r => r.supplier_name).filter(Boolean))].sort()
  , [data]);

  const filtered = useMemo(() =>
    !supplier ? data : data.filter(r => r.supplier_name === supplier)
  , [data, supplier]);

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/')}>← Home</button>
        <span className="page-nav__title">Customer Orders — No Stock</span>
        <button className="back-btn" onClick={() => navigate('/smart-buyer')}>🛒 Smart Buyer</button>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button className="app-header__btn" onClick={load}>↻ Refresh</button>
      </div>

      <section className="section">
        <SectionHeader
          title="Customer Orders — No Stock"
          count={filtered.length}
          onRefresh={load}
          loading={loading}
          actions={
            <select
              value={supplier}
              onChange={e => setSupplier(e.target.value)}
              style={{
                background: 'var(--bg3)', color: 'var(--white)',
                border: '1px solid var(--border2)', borderRadius: 4,
                padding: '4px 10px', fontFamily: 'var(--font-body)',
                fontSize: 12, cursor: 'pointer',
              }}
            >
              <option value="">All Suppliers</option>
              {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          }
        />
        <p className="section__desc">
          Open approved orders where available stock cannot fulfil the outstanding quantity. Click a row to see which customers are waiting.
          {supplier && <strong style={{ color: 'var(--gold)', marginLeft: 8 }}>Filtered: {supplier}</strong>}
        </p>
        <OrdersWithoutStockTable data={filtered} loading={loading} ordersByRow={ordersByRow} />
      </section>
    </main>
  );
};

export default CustomerOrdersNoStock;
